import { useEffect, useMemo, useState } from 'react'
import { Loader2, Plus, UserX } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useCreateProspect } from '@/api/powerDialer'
import { styleForCategory } from '@/lib/dispositionCategory'
import type {
  CampaignStats,
  PowerDialerProspect,
} from '@/types/powerDialer'

// Onglets (design Figma) : Tous / À rappeler / Faits.
const TABS: { value: string; labelKey: string; labelFr: string }[] = [
  { value: 'all', labelKey: 'tabAll', labelFr: 'Tous' },
  { value: 'callback', labelKey: 'tabCallback', labelFr: 'À rappeler' },
  { value: 'done', labelKey: 'tabDone', labelFr: 'Faits' },
]

function isCallbackProspect(p: PowerDialerProspect): boolean {
  return p.disposition?.category === 'callback' || p.status === 'callback'
}

function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  return phone
}

/** Heure courte du rappel : "08:40" (aujourd'hui), "Demain", sinon "DD/MM". */
function shortWhen(dateStr: string, locale: string, tomorrow: string): string {
  const d = new Date(dateStr)
  const now = new Date()
  const startOfDay = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate())
  const diff = Math.round(
    (startOfDay(d).getTime() - startOfDay(now).getTime()) / 86400000,
  )
  if (diff === 0)
    return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
  if (diff === 1) return tomorrow
  return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' })
}

function prospectName(p: PowerDialerProspect): string {
  return (
    p.full_name || `${p.first_name} ${p.last_name}`.trim() || p.phone
  )
}

function prospectInitials(p: PowerDialerProspect): string {
  return prospectName(p)
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

interface Props {
  campaignId: number
  prospects: PowerDialerProspect[]
  filteredProspects: PowerDialerProspect[]
  activeProspectId: number | null
  onSelectProspect: (id: number) => void
  filter: string
  onFilterChange: (filter: string) => void
  stats: CampaignStats | undefined
  darkMode: boolean
}

export function PdProspectsList({
  campaignId,
  prospects,
  filteredProspects,
  activeProspectId,
  onSelectProspect,
  filter,
  onFilterChange,
  stats,
  darkMode,
}: Props) {
  const { t, i18n } = useTranslation()
  const dateLocale = i18n.language?.startsWith('en') ? 'en-CA' : 'fr-CA'
  const [showAddProspect, setShowAddProspect] = useState(false)
  const [newProspectPhone, setNewProspectPhone] = useState('')
  const [newProspectName, setNewProspectName] = useState('')

  const createProspect = useCreateProspect(campaignId)

  // Tick toutes les minutes pour re-évaluer les callbacks dont l'heure arrive.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(t)
  }, [])

  // Callbacks dont l'heure est passée : groupe "À rappeler maintenant".
  const dueCallbacks = useMemo(
    () =>
      prospects.filter(
        (p) =>
          isCallbackProspect(p) &&
          p.callback_date &&
          new Date(p.callback_date).getTime() <= now,
      ),
    [prospects, now],
  )
  const dueCallbackIds = useMemo(
    () => new Set(dueCallbacks.map((p) => p.id)),
    [dueCallbacks],
  )
  // Retire les callbacks dus de la liste filtrée principale pour éviter le doublon.
  const remainingProspects = useMemo(
    () => filteredProspects.filter((p) => !dueCallbackIds.has(p.id)),
    [filteredProspects, dueCallbackIds],
  )

  const handleAddProspect = async () => {
    if (!newProspectPhone.trim() || createProspect.isPending) return
    const [firstName = '', ...rest] = newProspectName.trim().split(' ')
    const lastName = rest.join(' ')
    try {
      const created = await createProspect.mutateAsync({
        phone: newProspectPhone.trim(),
        first_name: firstName,
        last_name: lastName,
      })
      setShowAddProspect(false)
      setNewProspectPhone('')
      setNewProspectName('')
      onSelectProspect(created.id)
    } catch {
      // toast géré ailleurs
    }
  }

  const total = stats?.total_prospects || prospects.length
  const called = stats?.prospects_called || 0

  const border = darkMode ? 'border-slate-800' : 'border-slate-200'
  const inputBg = darkMode ? 'bg-slate-900' : 'bg-white'
  const textMuted = darkMode ? 'text-slate-500' : 'text-slate-400'
  const textDim = darkMode ? 'text-slate-600' : 'text-slate-400'
  const textSecondary = darkMode ? 'text-slate-300' : 'text-slate-600'
  const textPrimary = darkMode ? 'text-white' : 'text-slate-900'
  const rowBg = darkMode ? 'bg-slate-900' : 'bg-white'
  const rowSelBg = darkMode ? 'bg-slate-800' : 'bg-amber-50/60'
  const cardBg = darkMode ? 'bg-slate-900' : 'bg-white'

  /** Ligne prospect (design Figma) : avatar initiales + nom/société + méta droite. */
  const renderRow = (prospect: PowerDialerProspect) => {
    const isActive = prospect.id === activeProspectId
    const isFinal =
      prospect.is_final ??
      ['interested', 'not_interested', 'bad_number'].includes(prospect.status)
    const isCb = isCallbackProspect(prospect) && !!prospect.callback_date
    return (
      <button
        key={prospect.id}
        type="button"
        onClick={() => onSelectProspect(prospect.id)}
        className={cn(
          'w-full text-left px-2.5 py-2.5 flex items-center gap-2.5 rounded-xl transition-all border-l-[3px]',
          isActive
            ? cn('border-[#F5A623]', rowSelBg)
            : cn('border-transparent', rowBg, darkMode ? 'hover:bg-slate-800/60' : 'hover:bg-slate-50'),
        )}
      >
        <div
          className={cn(
            'w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0 text-[11px] font-bold',
            darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-600',
          )}
        >
          {prospectInitials(prospect)}
        </div>
        <div className="flex-1 min-w-0">
          <p
            className={cn(
              'text-[13px] font-semibold truncate',
              isActive ? textPrimary : textSecondary,
            )}
          >
            {prospectName(prospect)}
          </p>
          <p className={cn('text-[11px] truncate', textDim)}>
            {prospect.company_name || formatPhone(prospect.phone)}
          </p>
        </div>
        {isFinal ? (
          <span className={cn('text-[11px] font-medium shrink-0 flex items-center gap-1', textDim)}>
            <span>✓</span> {t('powerDialer.session.doneMeta', 'Fait')}
          </span>
        ) : isCb ? (
          <span className={cn('text-[11px] font-medium shrink-0', textSecondary)}>
            {shortWhen(prospect.callback_date as string, dateLocale, t('powerDialer.session.tomorrow', 'Demain'))}
          </span>
        ) : prospect.disposition ? (
          // Disposition intermédiaire (messagerie, neutre…) : pastille de
          // catégorie + libellé, pour garder le repère visuel d'un coup d'œil.
          <span className="text-[11px] font-medium shrink-0 flex items-center gap-1.5 max-w-[92px]">
            <span
              className={cn(
                'w-1.5 h-1.5 rounded-full shrink-0',
                styleForCategory(prospect.disposition.category).dot,
              )}
            />
            <span className={cn('truncate', textDim)}>
              {prospect.disposition.name}
            </span>
          </span>
        ) : null}
      </button>
    )
  }

  return (
    <div className="w-[280px] flex flex-col shrink-0 p-4 gap-3.5 min-h-0">
      {/* Titre + compteur + ajouter (design Figma) */}
      <div className="flex items-center gap-2">
        <h2 className={cn('text-[15px] font-semibold flex-1', textPrimary)}>
          {t('powerDialer.session.callQueue', "File d'appel")}
        </h2>
        <span
          className={cn(
            'px-2 py-0.5 rounded-full text-[11px] font-semibold',
            darkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500',
          )}
        >
          {total}
        </span>
        <button
          type="button"
          onClick={() => setShowAddProspect((v) => !v)}
          title={t('powerDialer.session.addProspect', 'Ajouter un prospect')}
          className={cn(
            'w-6 h-6 rounded-md flex items-center justify-center transition-colors',
            darkMode
              ? 'text-slate-500 hover:text-white hover:bg-slate-800'
              : 'text-slate-400 hover:text-slate-900 hover:bg-slate-100',
          )}
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Onglets Tous / À rappeler / Faits */}
      <div className="flex items-center gap-1.5">
        {TABS.map((tab) => {
          const active = filter === tab.value
          return (
            <button
              key={tab.value}
              type="button"
              onClick={() => onFilterChange(tab.value)}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs transition-colors border',
                active
                  ? darkMode
                    ? 'bg-slate-800 border-white/10 text-white font-semibold'
                    : 'bg-white border-slate-300 text-slate-900 font-semibold shadow-sm'
                  : darkMode
                    ? 'bg-slate-900 border-transparent text-slate-400 font-medium hover:text-slate-300'
                    : 'bg-slate-100 border-transparent text-slate-500 font-medium hover:text-slate-700',
              )}
            >
              {t(`powerDialer.session.${tab.labelKey}`, tab.labelFr)}
            </button>
          )
        })}
      </div>

      {/* Formulaire d'ajout (replié par défaut) */}
      {showAddProspect && (
        <div className={cn('p-3 rounded-xl border space-y-2', cardBg, border)}>
          <input
            type="tel"
            value={newProspectPhone}
            onChange={(e) => setNewProspectPhone(e.target.value)}
            placeholder={t('powerDialer.session.phoneRequired', 'Numéro de téléphone *')}
            className={cn(
              'w-full text-xs rounded-lg border px-2.5 py-1.5 outline-none focus:border-[#F5A623]',
              inputBg,
              border,
              textPrimary,
            )}
            autoFocus
          />
          <input
            type="text"
            value={newProspectName}
            onChange={(e) => setNewProspectName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleAddProspect()
            }}
            placeholder={t('powerDialer.session.nameOptional', 'Nom (optionnel)')}
            className={cn(
              'w-full text-xs rounded-lg border px-2.5 py-1.5 outline-none focus:border-[#F5A623]',
              inputBg,
              border,
              textPrimary,
            )}
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void handleAddProspect()}
              disabled={!newProspectPhone.trim() || createProspect.isPending}
              className="flex-1 text-xs py-1.5 bg-[#F5A623] hover:bg-[#e0951f] disabled:opacity-40 text-[#1A2233] rounded-lg font-semibold transition-colors flex items-center justify-center gap-1"
            >
              {createProspect.isPending ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                t('powerDialer.session.add', 'Ajouter')
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAddProspect(false)
                setNewProspectPhone('')
                setNewProspectName('')
              }}
              className={cn(
                'px-3 text-xs py-1.5 rounded-lg',
                darkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500',
              )}
            >
              {t('powerDialer.session.cancel', 'Annuler')}
            </button>
          </div>
        </div>
      )}

      {/* Carte PROGRESSION (design Figma) */}
      <div className={cn('rounded-xl border px-3.5 py-3', cardBg, border)}>
        <div className="flex items-center justify-between mb-2">
          <p className={cn('text-[10px] font-semibold uppercase tracking-widest', textMuted)}>
            {t('powerDialer.session.progress', 'Progression')}
          </p>
          <p className={cn('text-[11px] font-semibold', textSecondary)}>
            {called} / {total}
          </p>
        </div>
        <div
          className={cn(
            'h-1.5 rounded-full overflow-hidden',
            darkMode ? 'bg-slate-800' : 'bg-slate-100',
          )}
        >
          <div
            className="h-full rounded-full bg-[#F5A623] transition-all"
            style={{ width: total ? `${Math.min(100, (called / total) * 100)}%` : '0%' }}
          />
        </div>
      </div>

      {/* Liste groupée */}
      <div className="flex-1 overflow-y-auto min-h-0 space-y-3.5">
        {dueCallbacks.length > 0 && (
          <div className="space-y-1.5">
            <p className={cn('text-[10px] font-semibold uppercase tracking-widest', textMuted)}>
              {t('powerDialer.session.callbackNow', 'À rappeler maintenant')}
            </p>
            {dueCallbacks.map(renderRow)}
          </div>
        )}

        {remainingProspects.length === 0 && dueCallbacks.length === 0 ? (
          <div className="p-6 text-center">
            <UserX className={cn('w-8 h-8 mx-auto opacity-30', textMuted)} />
            <p className={cn('text-xs mt-2', textMuted)}>{t('powerDialer.session.noProspect', 'Aucun prospect')}</p>
          </div>
        ) : (
          remainingProspects.length > 0 && (
            <div className="space-y-1.5">
              {dueCallbacks.length > 0 && (
                <p className={cn('text-[10px] font-semibold uppercase tracking-widest', textMuted)}>
                  {t('powerDialer.session.later', 'Plus tard')}
                </p>
              )}
              {remainingProspects.map(renderRow)}
            </div>
          )
        )}
      </div>
    </div>
  )
}
